import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { SalaryData } from "@/actions/ssr/salary/getSalarySSR";

const FALLBACK: SalaryData = {
  kpis: {
    net_salary: "—",
    taxes_deductions: "—",
    last_salary_date: "—",
    total_earnings_year: "—",
  },
  payslip: {
    title: "—",
    status: "Pending",
    earnings: [],
    bonuses: [],
    gross_total: "—",
    tax_label: "",
    tax_amount: "—",
    net_salary: "—",
  },
  history: [],
};

export interface SalaryDateFilter {
  from: string | null;
  to: string | null;
}

interface StaffSalaryState {
  data: SalaryData;
  clientFetched: boolean;
  /** Page-level date range the user picked. Wired to the history card. */
  dateFilter: SalaryDateFilter;
}

const initialState: StaffSalaryState = {
  data: FALLBACK,
  clientFetched: false,
  dateFilter: { from: null, to: null },
};

const staffSalarySlice = createSlice({
  name: "staffSalary",
  initialState,
  reducers: {
    setStaffSalaryFromSSR(state, action: PayloadAction<SalaryData>) {
      if (state.clientFetched) return;
      state.data = action.payload;
    },
    setStaffSalary(state, action: PayloadAction<SalaryData>) {
      state.clientFetched = true;
      state.data = action.payload;
    },
    /**
     * Page-level date filter. Dispatched by the page header's
     * `<CDatePickerWithPresets>` and consumed by `<StaffSalaryHistoryCard>`
     * via the `useFilteredFetch` `globalFilterSelector`.
     *
     * BE honours `date_from` / `date_to` (verified v0.73, 2026-06-09 —
     * see `staff-v2/me-payroll.md` §1.3). Filters on `period_end` with a
     * 90-day cap. Over-cap / bad format / `date_from > date_to` → 400
     * `invalid_date_range` with the same envelope shape as the chart and
     * the leaderboard.
     */
    setStaffSalaryDateFilter(state, action: PayloadAction<SalaryDateFilter>) {
      state.dateFilter = action.payload;
    },
  },
});

export const {
  setStaffSalaryFromSSR,
  setStaffSalary,
  setStaffSalaryDateFilter,
} = staffSalarySlice.actions;
export default staffSalarySlice.reducer;
